import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';

/** Chip-to-wizard-step mapping */
const FIELD_STEP = {
  province: 1,
  residencyYear: 1,
  objective: 2,
  income: 3,
  match: 3,
};

const STEP_TIPS = {
  1: 'calc.tips.step1',
  2: 'calc.tips.step2',
  3: 'calc.tips.step3',
  4: 'calc.tips.step4',
};

const precisionConfig = {
  low: { bg: '#FEF9E7', color: '#A16207', border: '#FDE68A', pct: 20 },
  medium: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', pct: 55 },
  high: { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0', pct: 80 },
  very_high: { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0', pct: 100 },
};

const calcPrecision = (data) => {
  const has = {
    province: !!data.province,
    residencyYear: !!data.yearsInCanada && data.yearsInCanada !== 'unknown',
    objective: !!data.objective,
    income: !!data.income && Number(data.income) > 0,
    match: !!data.employerMatch,
  };
  const filled = Object.values(has).filter(Boolean).length;
  const total = Object.keys(has).length;
  const firstMissing = Object.entries(has).find(([, v]) => !v)?.[0] || null;

  let level = 'low';
  if (filled >= 4) level = 'high';
  else if (filled >= 3) level = 'medium';
  if (filled === total) level = 'very_high';

  return { level, filled, total, firstMissing };
};

/* ── Chip subcomponents ── */

const DoneChip = ({ emoji, label, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      height: 28,
      pl: '8px',
      pr: '7px',
      borderRadius: '8px',
      bgcolor: '#ECFDF5',
      border: '1px solid #BBF7D0',
      cursor: onClick ? 'pointer' : 'default',
      userSelect: 'none',
      transition: 'all 0.15s',
      '&:hover': onClick ? { bgcolor: '#D1FAE5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' } : {},
    }}
  >
    <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }}>{emoji}</Box>
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: '#065F46', lineHeight: 1 }}>
      {label}
    </Typography>
    <CheckCircleIcon sx={{ fontSize: 13, color: '#10B981', ml: '-1px' }} />
  </Box>
);

const PendingChip = ({ label, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      height: 28,
      px: '8px',
      borderRadius: '8px',
      bgcolor: '#F9FAFB',
      border: '1px solid #E5E7EB',
      cursor: onClick ? 'pointer' : 'default',
      userSelect: 'none',
      transition: 'all 0.15s',
      '&:hover': onClick ? { bgcolor: '#F3F4F6', borderColor: '#D1D5DB' } : {},
    }}
  >
    <Typography component="span" sx={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1 }}>
      {label}
    </Typography>
  </Box>
);

const PrecisionBadge = ({ label, config }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 28,
      px: '10px',
      borderRadius: '8px',
      bgcolor: config.bg,
      border: `1px solid ${config.border}`,
    }}
  >
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: config.color, lineHeight: 1 }}>
      {label}
    </Typography>
  </Box>
);

/* ── Main component ── */

const StickyResumen = ({ data, currentStep, onGoToStep }) => {
  const { t } = useTranslation();
  const s = 'calc.summary';

  const { level, filled, total, firstMissing } = calcPrecision(data);
  const pCfg = precisionConfig[level];

  const objectiveLabels = {
    home: t('calc.objectives.home'),
    taxes: t('calc.objectives.taxes'),
    flexibility: t('calc.objectives.flexibility'),
    retirement: t('calc.objectives.retirement'),
    balanced: t('calc.objectives.balanced'),
  };

  const matchLabels = {
    yes: t('calc.step3.matchYes'),
    no: t('calc.step3.matchNo'),
    unknown: t('calc.step3.matchUnknown'),
  };

  const fieldLabels = {
    province: t(`${s}.province`),
    residencyYear: t(`${s}.year`),
    objective: t(`${s}.objective`),
    income: t(`${s}.income`),
    match: t(`${s}.match`),
  };

  const chips = [
    {
      key: 'province',
      emoji: '\uD83D\uDCCD',
      value: data.province || null,
      display: data.province,
    },
    {
      key: 'residencyYear',
      emoji: '\uD83D\uDCC5',
      value: data.yearsInCanada && data.yearsInCanada !== 'unknown' ? data.yearsInCanada : null,
      display: data.yearsInCanada === 'before2020' ? '<2020' : data.yearsInCanada,
    },
    {
      key: 'objective',
      emoji: '\uD83C\uDFAF',
      value: data.objective || null,
      display: data.objective ? objectiveLabels[data.objective] : null,
    },
    {
      key: 'income',
      emoji: '\uD83D\uDCB5',
      value: data.income && Number(data.income) > 0 ? data.income : null,
      display: data.income ? `$${Number(data.income).toLocaleString('en-CA')}` : null,
    },
    {
      key: 'match',
      emoji: '\uD83C\uDFE2',
      value: data.employerMatch || null,
      display: data.employerMatch ? matchLabels[data.employerMatch] : null,
    },
  ];

  const levelKey = level === 'very_high' ? 'veryHigh' : level;
  const goTo = (step) => onGoToStep ? () => onGoToStep(step) : undefined;

  const tipKey = STEP_TIPS[currentStep] || STEP_TIPS[1];

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 90,
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 340,
      }}
    >
      {/* Thin top accent bar */}
      <Box sx={{ height: 3, background: 'linear-gradient(90deg, #10B981 0%, #3B82F6 50%, #F59E0B 100%)', flexShrink: 0 }} />

      {/* Profile section */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500, mb: 2 }}>
          {t(`${s}.title`)}
        </Typography>

        {/* Chip grid */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: 2 }}>
          {chips.map((chip) => {
            const done = !!chip.value;
            return done ? (
              <DoneChip
                key={chip.key}
                emoji={chip.emoji}
                label={chip.display}
                onClick={goTo(FIELD_STEP[chip.key])}
              />
            ) : (
              <PendingChip
                key={chip.key}
                label={`${fieldLabels[chip.key]}: \u2014`}
                onClick={goTo(FIELD_STEP[chip.key])}
              />
            );
          })}
        </Box>

        {/* Precision row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <PrecisionBadge
            label={`${t(`${s}.precision`)}: ${t(`${s}.${levelKey}`)}`}
            config={pCfg}
          />
          <LinearProgress
            variant="determinate"
            value={pCfg.pct}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: '#F3F4F6',
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                bgcolor: pCfg.color,
                transition: 'transform 0.5s ease',
              },
            }}
          />
          <Typography sx={{ fontSize: 11, color: '#9CA3AF', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {filled}/{total}
          </Typography>
        </Box>

        {/* Guidance hint */}
        {firstMissing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
            <Typography sx={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.4 }}>
              {t(`${s}.missingFields`, { field: fieldLabels[firstMissing] || firstMissing })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Contextual tip — fills bottom space */}
      <Box
        sx={{
          mx: 2.5,
          mb: 2.5,
          p: 2,
          bgcolor: '#F7F9FC',
          borderRadius: '10px',
          border: '1px solid #EEF0F3',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 16, color: '#F59E0B', mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.6 }}>
            {t(tipKey, { defaultValue: t('calc.tips.step1') })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default StickyResumen;
